import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  MessageSquareText,
  PackageCheck,
  Star,
  Truck,
} from "lucide-react";
import "./FoodDeliveryFeedback.css";

type WhatWentWell = "delivery-associate" | "packaging" | "other" | "";
type AssociateFeedback = "polite-behaviour" | "";
type PackagingFeedback =
  | "sturdy-packaging"
  | "appropriate-packaging"
  | "recyclable-packaging"
  | "";

const PACKAGING_OPTIONS: { value: PackagingFeedback; label: string }[] = [
  { value: "sturdy-packaging", label: "Sturdy packaging" },
  { value: "appropriate-packaging", label: "Appropriate packaging" },
  { value: "recyclable-packaging", label: "100% recyclable packaging" },
];

const FoodDeliveryFeedback = () => {
  const navigate = useNavigate();

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [whatWentWell, setWhatWentWell] = useState<WhatWentWell>("");
  const [associateFeedback, setAssociateFeedback] =
    useState<AssociateFeedback>("");
  const [packagingFeedback, setPackagingFeedback] =
    useState<PackagingFeedback>("");
  const [comments, setComments] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const activeRating = hoveredRating || rating;
  const goBack = () => navigate("/FoodOrderList");

  const handleWhatWentWell = (value: WhatWentWell) => {
    setWhatWentWell(value);
    if (value !== "delivery-associate") {
      setAssociateFeedback("");
    }
    if (value !== "packaging") {
      setPackagingFeedback("");
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (rating === 0) {
      setError("Please select a delivery rating.");
      return;
    }
    if (!whatWentWell) {
      setError("Please select what went well with this delivery.");
      return;
    }
    if (whatWentWell === "delivery-associate" && !associateFeedback) {
      setError("Please select feedback for the delivery associate.");
      return;
    }
    if (whatWentWell === "packaging" && !packagingFeedback) {
      setError("Please select feedback for the packaging.");
      return;
    }

    setError("");
    setSubmitted(true);
  };

  const closeSuccess = () => {
    setSubmitted(false);
    goBack();
  };

  return (
    <section className="food-delivery-feedback-page">
      <div className="food-delivery-feedback-shell">
        <button
          type="button"
          className="food-delivery-feedback-back"
          onClick={goBack}
        >
          <ArrowLeft size={18} strokeWidth={2.1} />
          <span>Back to Food Orders</span>
        </button>

        <header className="food-delivery-feedback-header">
          <div className="food-delivery-feedback-heading">
            <span className="food-delivery-feedback-heading-icon">
              <Truck size={22} strokeWidth={2} />
            </span>
            <div>
              <span className="food-delivery-feedback-kicker">
                FOOD & DINING
              </span>
              <h1>Delivery Feedback</h1>
              <p>
                Rate the delivery experience and tell us what went well.
              </p>
            </div>
          </div>
        </header>

        <div className="food-delivery-feedback-layout">
          <aside className="food-delivery-feedback-summary-card">
            <div className="food-delivery-feedback-restaurant">
              <div className="food-delivery-feedback-image">
                <img
                  src="../../assets/img/food/food-2.jpg"
                  alt="Restaurant"
                  loading="lazy"
                />
              </div>
              <div className="food-delivery-feedback-restaurant-copy">
                <span>RESTAURANT</span>
                <h2>Your delivery</h2>
                <p>Share your delivery experience</p>
                <span className="food-delivery-feedback-delivered-pill">
                  <PackageCheck size={13} strokeWidth={2} />
                  DELIVERED
                </span>
              </div>
            </div>
            <div className="food-delivery-feedback-summary-divider" />
            <div className="food-delivery-feedback-meta-grid">
              <div className="food-delivery-feedback-meta-card">
                <span className="food-delivery-feedback-meta-icon">
                  <MessageSquareText size={17} strokeWidth={2} />
                </span>
                <div>
                  <span>FEEDBACK</span>
                  <strong>Delivery experience</strong>
                </div>
              </div>
            </div>
          </aside>

          <form
            className="food-delivery-feedback-form"
            onSubmit={handleSubmit}
          >
            <section className="food-delivery-feedback-section">
              <div className="food-delivery-feedback-section-head">
                <div>
                  <span className="food-delivery-feedback-section-label">
                    DELIVERY RATING
                  </span>
                  <h2>Rate your delivery experience</h2>
                  <p>Select a rating from 1 to 5 stars.</p>
                </div>
                <span className="food-delivery-feedback-step">01</span>
              </div>

              <div
                className="food-delivery-feedback-stars"
                onMouseLeave={() => setHoveredRating(0)}
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    type="button"
                    key={value}
                    className={value <= activeRating ? "active" : ""}
                    onMouseEnter={() => setHoveredRating(value)}
                    onClick={() => setRating(value)}
                    aria-label={`Rate delivery ${value} star`}
                    aria-pressed={rating === value}
                  >
                    <Star size={31} strokeWidth={1.8} />
                  </button>
                ))}
              </div>
            </section>

            <section className="food-delivery-feedback-section">
              <div className="food-delivery-feedback-section-head">
                <div>
                  <span className="food-delivery-feedback-section-label">
                    DELIVERY HIGHLIGHTS
                  </span>
                  <h2>What went well?</h2>
                  <p>
                    Choose the part of your delivery experience you want to
                    highlight.
                  </p>
                </div>
                <span className="food-delivery-feedback-step">02</span>
              </div>

              <div className="food-delivery-feedback-choice-grid">
                {(
                  [
                    { value: "delivery-associate", label: "Delivery associate" },
                    { value: "packaging", label: "Packaging" },
                    { value: "other", label: "Other" },
                  ] as { value: WhatWentWell; label: string }[]
                ).map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    className={
                      whatWentWell === option.value ? "selected" : ""
                    }
                    onClick={() => handleWhatWentWell(option.value)}
                  >
                    <span>
                      {whatWentWell === option.value && (
                        <Check size={14} strokeWidth={3} />
                      )}
                    </span>
                    {option.label}
                  </button>
                ))}
              </div>
            </section>

            {whatWentWell === "delivery-associate" && (
              <section className="food-delivery-feedback-section">
                <div className="food-delivery-feedback-section-head">
                  <div>
                    <span className="food-delivery-feedback-section-label">
                      DELIVERY ASSOCIATE
                    </span>
                    <h2>Tell us more about the delivery associate</h2>
                  </div>
                  <span className="food-delivery-feedback-step">03</span>
                </div>
                <div className="food-delivery-feedback-choice-grid">
                  <button
                    type="button"
                    className={
                      associateFeedback === "polite-behaviour"
                        ? "selected"
                        : ""
                    }
                    onClick={() =>
                      setAssociateFeedback("polite-behaviour")
                    }
                  >
                    <span>
                      {associateFeedback === "polite-behaviour" && (
                        <Check size={14} strokeWidth={3} />
                      )}
                    </span>
                    Polite behaviour
                  </button>
                </div>
              </section>
            )}

            {whatWentWell === "packaging" && (
              <section className="food-delivery-feedback-section">
                <div className="food-delivery-feedback-section-head">
                  <div>
                    <span className="food-delivery-feedback-section-label">
                      PACKAGING
                    </span>
                    <h2>Tell us more about the packaging</h2>
                  </div>
                  <span className="food-delivery-feedback-step">03</span>
                </div>
                <div className="food-delivery-feedback-choice-grid">
                  {PACKAGING_OPTIONS.map((option) => (
                    <button
                      type="button"
                      key={option.value}
                      className={
                        packagingFeedback === option.value
                          ? "selected"
                          : ""
                      }
                      onClick={() => setPackagingFeedback(option.value)}
                    >
                      <span>
                        {packagingFeedback === option.value && (
                          <Check size={14} strokeWidth={3} />
                        )}
                      </span>
                      {option.label}
                    </button>
                  ))}
                </div>
              </section>
            )}

            <section className="food-delivery-feedback-section">
              <div className="food-delivery-feedback-section-head">
                <div>
                  <span className="food-delivery-feedback-section-label">
                    COMMENTS
                  </span>
                  <h2>Tell us more</h2>
                  <p>
                    Your feedback will be used to identify improvements but
                    not responded to directly. If you need immediate
                    assistance with an order, contact us.
                  </p>
                </div>
                <span className="food-delivery-feedback-step">04</span>
              </div>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                maxLength={500}
                placeholder="Tell us more"
                aria-label="Delivery feedback comments"
              />
            </section>

            {error && (
              <div
                className="food-delivery-feedback-error"
                role="alert"
              >
                {error}
              </div>
            )}

            <footer className="food-delivery-feedback-form-footer">
              <button
                type="button"
                className="secondary"
                onClick={goBack}
              >
                Cancel
              </button>
              <button type="submit" className="primary">
                Submit Feedback
              </button>
            </footer>
          </form>
        </div>
      </div>

      {submitted && (
        <div className="food-delivery-feedback-success-overlay">
          <section
            className="food-delivery-feedback-success-dialog"
            role="alertdialog"
            aria-modal="true"
          >
            <span className="food-delivery-feedback-success-icon">
              <Check size={30} strokeWidth={2.5} />
            </span>
            <h2>Feedback Submitted</h2>
            <p>Thanks for sharing your delivery experience.</p>
            <button type="button" onClick={closeSuccess}>
              Back to Food Orders
            </button>
          </section>
        </div>
      )}
    </section>
  );
};

export default FoodDeliveryFeedback;
